import { DataTableColumnHeader } from '@/components/dataTable/ColumnHeader';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { formatDate } from '@/lib/dateFormatter';
import { formatAmount } from '@/lib/format';
import type { CustomerReferralDetail } from '@/services/dashboard/referralReports/referralReports.types';
import type { ColumnDef } from '@tanstack/react-table';

const textCell = (value: string | number) => (
  <div className="whitespace-nowrap text-xs font-medium text-foreground-detail">{value}</div>
);

export const getAffiliateCustomerColumns = (t: (key: string) => string): ColumnDef<CustomerReferralDetail>[] => [
  {
    accessorKey: 'sn',
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.sn')} />,
    cell: ({ row, table }) => textCell(getSerialNumber(table, row.index)),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: 'reservedBy',
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.reservedBy')} />,
    cell: ({ row }) => textCell(row.getValue<string>('reservedBy')),
  },
  {
    accessorKey: 'storeName',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('common.page.tableColumn.referralStoreName')} />
    ),
    cell: ({ row }) => (
      <div className="flex max-w-[20rem] items-center gap-3">
        {row.original.clubImage?.path && (
          <img
            src={row.original.clubImage.path}
            alt={row.original.clubImage.alt ?? row.original.storeName}
            className="h-8 w-8 shrink-0 rounded-full object-cover"
          />
        )}
        <div className="flex min-w-0 flex-col gap-1 whitespace-nowrap">
          <span className="max-w-[15rem] truncate text-xs font-medium text-foreground-subtle">
            {row.getValue<string>('storeName')}
          </span>
          <span className="truncate text-xs font-medium text-muted-foreground">{row.original.storeNameJp}</span>
        </div>
      </div>
    ),
  },
  {
    id: 'createdDate',
    accessorFn: (row) => row.visitDateTime,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('common.page.tableColumn.visitDateTime')} />
    ),
    cell: ({ row }) => textCell(formatDate(row.getValue<number>('createdDate'), 'YYYY/MM/DD HH:mm:ss')),
    enableColumnFilter: false,
  },
  {
    accessorKey: 'rewardPoints',
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.rewardPoints')} />,
    cell: ({ row }) => (
      <div className="whitespace-nowrap text-right text-xs font-medium text-foreground-detail">
        {formatAmount(Number(row.getValue<string>('rewardPoints')), ' ')} 円
      </div>
    ),
    meta: { align: 'right' },
  },
];
